'use strict';

const STORAGE_KEY = 'site_notice_dismissed';
const SHOW_DELAY_MS = 1500;

/**
 * Preview query: `?preview_notice=1` (active) or `?preview_notice=sn_…` (specific id).
 * @returns {{ mode: boolean, id: string|null }}
 */
function getPreviewState() {
    try {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('preview_notice');
        if (raw === null || raw === '') {
            return { mode: false, id: null };
        }
        if (raw === '1') {
            return { mode: true, id: null };
        }
        return { mode: true, id: raw };
    } catch (e) {
        return { mode: false, id: null };
    }
}

/**
 * @returns {boolean}
 */
function isPreviewMode() {
    return getPreviewState().mode;
}

/**
 * @param {string} id
 * @returns {boolean}
 */
function isDismissed(id) {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === id;
    } catch (e) {
        return false;
    }
}

/**
 * @param {string} id
 */
function markDismissed(id) {
    try {
        window.localStorage.setItem(STORAGE_KEY, id);
    } catch (e) {
        // private mode / quota — notice may reappear; acceptable
    }
}

/**
 * Sanitize HTML: allow only <strong>, <em>, <a href>.
 * @param {string} html
 * @returns {DocumentFragment}
 */
function sanitizeBody(html) {
    const template = document.createElement('template');
    template.innerHTML = html || '';
    const fragment = document.createDocumentFragment();

    /**
     * @param {Node} node
     * @param {Node} parent
     */
    function walk(node, parent) {
        if (node.nodeType === Node.TEXT_NODE) {
            parent.appendChild(document.createTextNode(node.textContent || ''));
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        const el = /** @type {Element} */ (node);
        const tag = el.tagName.toUpperCase();

        if (tag === 'STRONG' || tag === 'EM' || tag === 'P' || tag === 'BR') {
            const clean = document.createElement(tag.toLowerCase());
            Array.from(el.childNodes).forEach((child) => walk(child, clean));
            parent.appendChild(clean);
            return;
        }

        if (tag === 'A') {
            const href = el.getAttribute('href') || '';
            if (isSafeHref(href)) {
                const a = document.createElement('a');
                a.setAttribute('href', href);
                a.setAttribute('rel', 'noopener');
                if (/^https?:\/\//i.test(href)) {
                    a.setAttribute('target', '_blank');
                }
                Array.from(el.childNodes).forEach((child) => walk(child, a));
                parent.appendChild(a);
                return;
            }
            // Unsafe link — keep text only
            Array.from(el.childNodes).forEach((child) => walk(child, parent));
            return;
        }

        // Disallowed tag — keep children
        Array.from(el.childNodes).forEach((child) => walk(child, parent));
    }

    Array.from(template.content.childNodes).forEach((child) => walk(child, fragment));
    return fragment;
}

/**
 * @param {string} href
 * @returns {boolean}
 */
function isSafeHref(href) {
    if (!href || /^\s*(javascript|data|vbscript):/i.test(href)) {
        return false;
    }
    return /^(https?:|mailto:|tel:|\/|\.\/|\.\.\/|#)/i.test(href);
}

/**
 * @param {string|null} [previewId]
 * @returns {string}
 */
function apiUrl(previewId) {
    const root = (typeof config !== 'undefined' && config.rootUrl) ? config.rootUrl : '/';
    const base = root.endsWith('/') ? root : root + '/';
    const lang = (typeof config !== 'undefined' && config.lang) ? config.lang : '';
    // lang=en maps to the non-default ProcessWire language; bg/default omit or send default
    let url = base + '?action=site_notice';
    if (lang && lang !== 'bg' && lang !== 'default') {
        url += '&lang=' + encodeURIComponent(lang);
    }
    if (previewId) {
        url += '&preview_id=' + encodeURIComponent(previewId);
    }
    return url;
}

/**
 * @param {{
 *   id: string,
 *   mode?: string,
 *   title: string,
 *   body: string,
 *   button_label?: string,
 *   button_url?: string,
 *   image_url?: string,
 *   image_alt?: string
 * }} notice
 * @param {HTMLElement} root
 */
function openDialog(notice, root) {
    const previouslyFocused = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    root.hidden = false;
    root.innerHTML = '';

    const imageMode = notice.mode === 'image' && notice.image_url;

    const overlay = document.createElement('div');
    overlay.className = 'site-notice__overlay';
    overlay.setAttribute('data-site-notice-overlay', '');

    const dialog = document.createElement('div');
    dialog.className = 'site-notice__dialog' + (imageMode ? ' site-notice__dialog--image' : '');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'site-notice-title');

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'site-notice__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '<span aria-hidden="true">&times;</span>';
    dialog.appendChild(closeBtn);

    if (imageMode) {
        // Strict image-only: holiday card with wishes baked into the artwork
        const title = document.createElement('h2');
        title.id = 'site-notice-title';
        title.className = 'site-notice__title site-notice__title--sr';
        title.textContent = notice.image_alt || notice.title || 'Notice';
        dialog.appendChild(title);

        const figure = document.createElement('div');
        figure.className = 'site-notice__image-wrap';
        const img = document.createElement('img');
        img.className = 'site-notice__image';
        img.src = notice.image_url;
        img.alt = notice.image_alt || notice.title || '';
        img.decoding = 'async';
        img.loading = 'eager';
        figure.appendChild(img);
        dialog.appendChild(figure);
    } else {
        const title = document.createElement('h2');
        title.id = 'site-notice-title';
        title.className = 'site-notice__title';
        title.textContent = notice.title || '';
        dialog.appendChild(title);

        const body = document.createElement('div');
        body.className = 'site-notice__body';
        body.appendChild(sanitizeBody(notice.body || ''));
        dialog.appendChild(body);

        if (notice.button_label && notice.button_url && isSafeHref(notice.button_url)) {
            const actions = document.createElement('div');
            actions.className = 'site-notice__actions';
            const link = document.createElement('a');
            link.className = 'site-notice__button';
            link.href = notice.button_url;
            link.textContent = notice.button_label;
            link.rel = 'noopener';
            if (/^https?:\/\//i.test(notice.button_url)) {
                link.target = '_blank';
            }
            actions.appendChild(link);
            dialog.appendChild(actions);
        }
    }

    overlay.appendChild(dialog);
    root.appendChild(overlay);

    // Trigger CSS enter transition after paint
    window.requestAnimationFrame(() => {
        overlay.classList.add('is-visible');
    });

    // Focus management
    const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusable = () => Array.from(dialog.querySelectorAll(focusableSelector))
        .filter((el) => el instanceof HTMLElement && !el.hasAttribute('disabled'));

    closeBtn.focus();

    /**
     * @param {boolean} [persistDismiss]
     */
    function close(persistDismiss = true) {
        if (persistDismiss && notice.id && !isPreviewMode()) {
            markDismissed(notice.id);
        }
        document.removeEventListener('keydown', onKeyDown, true);
        overlay.removeEventListener('click', onOverlayClick);
        root.innerHTML = '';
        root.hidden = true;
        document.body.classList.remove('site-notice-open');
        if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
            previouslyFocused.focus();
        }
    }

    /**
     * @param {KeyboardEvent} event
     */
    function onKeyDown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            close(true);
            return;
        }
        if (event.key !== 'Tab') {
            return;
        }
        const focusable = getFocusable();
        if (focusable.length === 0) {
            event.preventDefault();
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    /**
     * @param {MouseEvent} event
     */
    function onOverlayClick(event) {
        if (event.target === overlay) {
            close(true);
        }
    }

    closeBtn.addEventListener('click', () => close(true));
    overlay.addEventListener('click', onOverlayClick);
    document.addEventListener('keydown', onKeyDown, true);
    document.body.classList.add('site-notice-open');
}

export function init() {
    const root = document.querySelector('[data-site-notice-root]');
    if (!(root instanceof HTMLElement)) {
        return;
    }

    const preview = getPreviewState();
    // Specific-id preview: no delay so admins see the message immediately
    const delay = preview.id ? 200 : SHOW_DELAY_MS;

    fetch(apiUrl(preview.id), {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
    })
        .then((res) => {
            if (!res.ok) {
                throw new Error('site_notice ' + res.status);
            }
            return res.json();
        })
        .then((data) => {
            if (!data || !data.active || !data.notice || !data.notice.id) {
                return;
            }
            const n = data.notice;
            // Need either image or text content
            const hasImage = n.mode === 'image' && n.image_url;
            const hasText = !!(n.title || n.body);
            if (!hasImage && !hasText) {
                return;
            }
            // Preview never writes dismiss; also skip dismiss check
            if (!preview.mode && isDismissed(n.id)) {
                return;
            }
            window.setTimeout(() => {
                openDialog(n, root);
            }, delay);
        })
        .catch(() => {
            // Silent fail — notice is non-critical
        });
}
